import { Injectable } from '@angular/core';
import {
	Action,
	NgxsOnInit,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { Session, SessionService } from '@services/session/session.service';
import { AuthState } from '@state/auth/auth.state';
import { UrlChanged } from '@state/navigation/navigation.actions';
import { UserProfileState } from '@state/user-profile';
import { filter, map, switchMap, tap } from 'rxjs/operators';

export interface SessionStateModel {
	sessions: Session[];
}

export { Session as UserSession };

@State<SessionStateModel>({
	name: 'userSessions',
	defaults: { sessions: [] },
})
@Injectable()
export class UserSessionsState implements NgxsOnInit {
	currentBusinessId: string;

	@Selector([UserSessionsState])
	static getSessions(state: SessionStateModel): Session[] {
		return state.sessions;
	}

	@Selector([UserSessionsState.getSessions])
	static getMostRecentSessions(sessions: Session[]): Session[] {
		return sessions.reduce((accum, curr) => {
			const { timestamp, userId } = curr;
			const last = accum.find((session) => session.userId === userId);
			if (!last) {
				accum.push(curr);
			} else if (last.timestamp < timestamp) {
				accum.splice(accum.indexOf(last), 1, curr);
			}

			return accum;
		}, []);
	}

	ngxsOnInit(ctx?: StateContext<SessionStateModel>) {
		this.store
			.select(UserProfileState.getCurrentBusinessId)
			.pipe(
				filter((currentBusinessId) => !!currentBusinessId),
				tap((currentBusinessId) => {
					this.currentBusinessId = currentBusinessId;
				}),
				switchMap((currentBusinessId) =>
					this.sessionService.getPresence(currentBusinessId),
				),
				map((presence) => (presence ? Object.values(presence) : [])),
			)
			.subscribe((sessions) => {
				ctx.patchState({ sessions });
			});
	}

	constructor(
		private sessionService: SessionService,
		private store: Store,
	) {}

	@Action(UrlChanged)
	urlChanged(
		_ctx: StateContext<UserSessionsState>,
		{ payload }: UrlChanged,
	): void {
		const userId = this.store.selectSnapshot(AuthState.getUserId);

		if (!(userId && this.currentBusinessId)) {
			console.log('Cannot write user session.');

			return;
		}

		this.sessionService.setNavigationUrl(
			userId,
			this.currentBusinessId,
			this.store.selectSnapshot(AuthState.getLoggedInUser)?.sessionId,
			payload.url,
			payload.matterId ?? null,
		);
	}
}
